import {ISelectOptions, ISelectParams} from "./index";
import * as ko from "knockout";
import {_} from 'lodash';
import {_d, getNestedObjectProperty, isEnabledComputedFactory, isPrimitive} from "../../util/misc";
import pubsub from "../../pubsub";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import {TBaseOrObservableType} from "../../index";

export abstract class SelectAbstract<T> {

    private readonly sortBy: ((obj: T, index?: number, array?: T[]) => string | number) | keyof T | (keyof T)[];
    protected data!: T[] | KnockoutComputed<T[]> | KnockoutObservableArray<T>
    protected readonly asc: boolean;
    protected readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    protected readonly options: Partial<ISelectOptions<T>>;
    protected readonly selected: KnockoutObservable<any>;
    protected value?: TBaseOrObservableType<T[keyof T]>
    private readonly icon: string | null;
    private readonly inline: boolean;
    private readonly isEnabled: KnockoutComputed<boolean>;

    constructor(protected params: ISelectParams<T>) {
        this.options = _.extend({
            caption: "—",
            defaultvalue: undefined,
            text: "name",
            value: null,
            title: null,
        }, params.options);

        this.isEnabled = isEnabledComputedFactory(params);

        this.asc = params.asc ?? true;

        if (!('selected' in params) && !('onSelected' in params)) {
            _d.error('Neither selected observable nor on select callback provided.');
        }
        this.selected = params.selected ?? ko.observable();

        if ('onSelected' in params && params.onSelected) {
            this.kosub.subscribeChanged(this.selected, params.onSelected);
        }

        this.sortBy = params.sortBy === undefined ? 'name' as keyof T : params.sortBy;
        this.icon = params.icon ?? null;
        this.inline = params.inline ?? true;

        if ('value' in params) {
            this.value = params.value;
        }
    }

    protected getInitialItem(data: T[], sel?: T): T | undefined {
        const selected = sel ?? this.selected();

        if (this.params.fnGetInitialItem) {
            return data.find(obj => this.params.fnGetInitialItem!(obj));
        }

        if (isPrimitive(selected)) {
            return selected as T;
        }

        if ('key' in this.params && this.params.key) {
            const key = this.params.key;
            const val = this.value ?? getNestedObjectProperty(selected, key);
            return data.find(obj => getNestedObjectProperty(obj, key) == val);
        }

        if (selected) {
            return data.find((item: any) => _.isEqual(item, selected));
        }

        if ('autoSelect' in this.params && this.getFiltered(data).length == 1) {
            return this.getFiltered(data)[0];
        }
        return undefined;
    }

    protected getFiltered(data: T[]): T[] {
        if ('fnFilter' in this.params && this.params.fnFilter) {
            return data.filter(this.params.fnFilter);
        }
        return data;
    }

    protected _sort(data: T[]): T[] {
        if (!this.sortBy) {
            return data;
        }

        const sort = this.sortBy;

        if (typeof sort == 'function') {
            return _.sortBy(data, sort);
        }

        if (Array.isArray(sort)) {
            return _.sortBy(data, (o: T) => {
                const key = sort.find(s => o[s]);
                return key ? o[key] : undefined;
            });
        }

        return _.sortBy(data, (o: T) => {
            const val = o[sort];
            if (typeof val == 'string') {
                return val.trim().toLowerCase();
            }
            return val;
        });
    }

    protected getStyle(obj: T) {
        return this.getAttr('style', obj)
    };

    protected getText(obj: T) {
        if (this.options.text === true) {
            return obj;
        }
        return this.getAttr('text', obj)
    };

    protected getTitle(obj: T) {
        return this.getAttr('title', obj)
    };

    protected getValue(obj: T) {
        if (_.isFunction(this.options.value)) {
            return this.options.value(obj);
        }
        return this.getValueFromKey(obj, this.options.value);
    };

    protected getAttr(attr: 'style' | 'text' | 'title' | 'value', obj: T): string | null {
        const key = this.options[attr];
        if (!key) {
            return null;
        }
        if (typeof key == 'function') {
            return key(obj);
        }
        return this.getValueFromKey(obj, key) ?? null;
    }

    protected getValueFromKey(item: any, key: string | string[]): string | undefined {
        if (!item) {
            return undefined;
        }
        if (!key) {
            return item;
        }
        if (Array.isArray(key)) {
            // todo: look at this
            const k = key.find(k => item[k]);
            if (!k) {
                return undefined;
            }
            return this.getValueFromPath(item, k) || item[k];
        }
        return this.getValueFromPath(item, key);
    }

    protected getValueFromPath(obj: any, key: string): any {
        if (typeof key === "string" && /\./.test(key)) {
            return getNestedObjectProperty(obj, key);
        }
        return obj[key];
    }

    dispose() {
        try {
            pubsub.off(this);
        } catch (err) {
        }
        this.kosub.unsuscribeAll();
        this.isEnabled.dispose();
    }

}